import { useState, useEffect, useCallback } from "react";
import { supabase, getOrganizationId } from "../lib/supabase";

export interface OrgBranding {
  id: string;
  organization_id: string;
  primary_color: string;
  secondary_color: string;
  accent_color: string;
  logo_url: string | null;
  logo_mini_url: string | null;
  favicon_url: string | null;
  app_name: string;
  company_name: string | null;
  font_family: string | null;
  theme_settings: Record<string, unknown>;
}

/** Default branding fallback (used when no DB record exists) */
const DEFAULT_BRANDING: OrgBranding = {
  id: "",
  organization_id: "",
  primary_color: "#1e40af",
  secondary_color: "#3b82f6",
  accent_color: "#06b6d4",
  logo_url: "/claims-app-logo.svg",
  logo_mini_url: "/claims-app-logo-mini.svg",
  favicon_url: null,
  app_name: "Claims App",
  company_name: null,
  font_family: "Inter",
  theme_settings: {},
};

/**
 * Fetches and applies the organization's white-label branding.
 * Injects CSS custom properties on the root element for theme-wide adoption.
 */
export function useBranding() {
  const [branding, setBranding] = useState<OrgBranding>(DEFAULT_BRANDING);
  const [loading, setLoading] = useState(true);

  const applyBrandingToDOM = useCallback((b: OrgBranding) => {
    const root = document.documentElement;
    root.style.setProperty("--brand-primary", b.primary_color);
    root.style.setProperty("--brand-secondary", b.secondary_color);
    root.style.setProperty("--brand-accent", b.accent_color);

    // Sidebar uses primary color as background
    root.style.setProperty("--sidebar-bg", b.primary_color);

    // Map to global color-primary for buttons, links, etc.
    root.style.setProperty("--color-primary", b.primary_color);

    // Extended font config from theme_settings
    const ts = b.theme_settings || {};
    const headerFont = (ts.header_font as string) || b.font_family || "Inter";
    const bodyFont = (ts.body_font as string) || b.font_family || "Inter";
    root.style.setProperty("--font-heading", `'${headerFont}', sans-serif`);
    root.style.setProperty("--font-body", `'${bodyFont}', sans-serif`);
    root.style.setProperty("--font-family", `'${bodyFont}', sans-serif`);

    // Load Google Fonts dynamically if not already loaded
    const fontsToLoad = new Set([headerFont, bodyFont]);
    fontsToLoad.forEach((font) => {
      if (font === "Inter") return; // Already in HTML
      const fontId = `gfont-${font.replace(/\s+/g, "-").toLowerCase()}`;
      if (!document.getElementById(fontId)) {
        const link = document.createElement("link");
        link.id = fontId;
        link.rel = "stylesheet";
        link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(font)}:wght@400;500;600;700&display=swap`;
        document.head.appendChild(link);
      }
    });

    // Update page title
    document.title = `${b.app_name} | ${b.company_name || "Claims Management"}`;

    // Update favicon if provided
    if (b.favicon_url) {
      let link = document.querySelector<HTMLLinkElement>("link[rel='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = b.favicon_url;
    }
  }, []);

  const fetchBranding = useCallback(async () => {
    const orgId = getOrganizationId();
    if (!orgId) {
      applyBrandingToDOM(DEFAULT_BRANDING);
      setLoading(false);
      return;
    }

    const { data, error } = await supabase
      .from("org_branding")
      .select("*")
      .eq("organization_id", orgId)
      .single();

    if (error || !data) {
      // No branding configured, use defaults
      applyBrandingToDOM(DEFAULT_BRANDING);
      setLoading(false);
      return;
    }

    const brandingData: OrgBranding = {
      id: data.id,
      organization_id: data.organization_id,
      primary_color: data.primary_color,
      secondary_color: data.secondary_color,
      accent_color: data.accent_color,
      logo_url: data.logo_url,
      logo_mini_url: data.logo_mini_url,
      favicon_url: data.favicon_url,
      app_name: data.app_name,
      company_name: data.company_name,
      font_family: data.font_family,
      theme_settings: (data.theme_settings as Record<string, unknown>) || {},
    };

    setBranding(brandingData);
    applyBrandingToDOM(brandingData);
    setLoading(false);
  }, [applyBrandingToDOM]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    fetchBranding();
  }, [fetchBranding]);

  return { branding, loading, refresh: fetchBranding };
}
